export interface RidgePoint {
  column: number;
  price: number;
}

/** Absolute price gaps. The comparison terrain links ridges 8 and 15 points apart. */
export interface RidgeFit {
  merge: number;
  link: number;
  minRun?: number;
}

function medianStep(strikes: number[]): number {
  if (strikes.length < 2) return 1;
  const gaps: number[] = [];
  for (let i = 1; i < strikes.length; i++) {
    gaps.push(Math.abs(strikes[i]! - strikes[i - 1]!));
  }
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || 1;
}

function minRun(nColumns: number): number {
  return Math.max(3, Math.round(nColumns * 0.12));
}

/** Drop a later price that sits on the same grid step as one already kept. */
function mergePrices(prices: number[], gap: number): number[] {
  const sorted = [...prices].sort((a, b) => a - b);
  const kept: number[] = [];
  for (const price of sorted) {
    const last = kept[kept.length - 1];
    if (last != null && price - last < gap) continue;
    kept.push(price);
  }
  return kept;
}

/**
 * Greedy nearest-neighbor tracks. A ridge may step one grid node per column.
 * Runs shorter than a few columns are dropped so a one-session wiggle does not
 * draw a line.
 */
function linkTracks(
  columns: number[][],
  linkGap: number,
  minRunCount?: number,
): RidgePoint[][] {
  const need = minRunCount ?? minRun(columns.length);
  const tracks: RidgePoint[][] = [];
  let active: { price: number; points: RidgePoint[] }[] = [];
  for (let column = 0; column < columns.length; column++) {
    const used = new Set<number>();
    const next: { price: number; points: RidgePoint[] }[] = [];
    for (const price of columns[column] ?? []) {
      let best: number | null = null;
      let bestDist = linkGap;
      for (let j = 0; j < active.length; j++) {
        if (used.has(j)) continue;
        const dist = Math.abs(price - active[j]!.price);
        if (dist < bestDist) {
          bestDist = dist;
          best = j;
        }
      }
      const point: RidgePoint = { column, price };
      if (best == null) {
        next.push({ price, points: [point] });
      } else {
        used.add(best);
        const prev = active[best]!;
        next.push({ price, points: [...prev.points, point] });
      }
    }
    for (let j = 0; j < active.length; j++) {
      if (!used.has(j) && active[j]!.points.length >= need) tracks.push(active[j]!.points);
    }
    active = next;
  }
  for (const track of active) {
    if (track.points.length >= need) tracks.push(track.points);
  }
  return tracks;
}

function columnValues(strikes: number[], field: number[][], column: number): number[] {
  return strikes.map((_, row) => field[row]?.[column] ?? 0);
}

/**
 * Local peaks (positive) or troughs (negative). A point counts only when it is
 * at least 30% of that column's extreme of the same sign, matching the terrain
 * overlay on the comparison chart. Edges are not extrema.
 */
export function gammaRidges(
  strikes: number[],
  field: number[][],
  positive: boolean,
  fit?: RidgeFit,
): RidgePoint[][] {
  const nCols = field[0]?.length ?? 0;
  if (strikes.length < 3 || nCols === 0) return [];
  const step = medianStep(strikes);
  const merge = fit?.merge ?? step * 0.6;
  const link = fit?.link ?? step * 1.6;
  const columns: number[][] = [];
  for (let column = 0; column < nCols; column++) {
    const values = columnValues(strikes, field, column);
    const n = Math.min(values.length, strikes.length);
    let extreme = 0;
    for (let i = 0; i < n; i++) {
      const value = values[i] ?? 0;
      extreme = positive ? Math.max(extreme, value) : Math.max(extreme, -value);
    }
    const found: number[] = [];
    if (extreme > 0) {
      const level = 0.3 * extreme;
      for (let i = 1; i < n - 1; i++) {
        const value = values[i] ?? 0;
        const left = values[i - 1] ?? 0;
        const right = values[i + 1] ?? 0;
        const hit = positive
          ? value >= level && value >= left && value >= right
          : value <= -level && value <= left && value <= right;
        if (hit) found.push(strikes[i]!);
      }
    }
    columns.push(mergePrices(found, merge));
  }
  return linkTracks(columns, link, fit?.minRun);
}

function zeroPrices(strikes: number[], column: number[]): number[] {
  const n = Math.min(strikes.length, column.length);
  const found: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const a = column[i] ?? 0;
    const b = column[i + 1] ?? 0;
    const strikeA = strikes[i]!;
    const strikeB = strikes[i + 1]!;
    if (a === 0 && b === 0) continue;
    if (a === 0) {
      found.push(strikeA);
      continue;
    }
    if ((a > 0 && b < 0) || (a < 0 && b > 0) || b === 0) {
      const span = a - b;
      const t = span === 0 ? 0 : a / span;
      found.push(strikeA + t * (strikeB - strikeA));
    }
  }
  return found;
}

/** Interpolated zero crossings, linked the same way as the ridges. */
export function zeroContours(
  strikes: number[],
  field: number[][],
  fit?: RidgeFit,
): RidgePoint[][] {
  const nCols = field[0]?.length ?? 0;
  if (strikes.length < 2 || nCols === 0) return [];
  const step = medianStep(strikes);
  const merge = fit?.merge ?? step * 0.6;
  const link = fit?.link ?? step * 1.6;
  const columns: number[][] = [];
  for (let column = 0; column < nCols; column++) {
    columns.push(mergePrices(zeroPrices(strikes, columnValues(strikes, field, column)), merge));
  }
  return linkTracks(columns, link, fit?.minRun);
}
